import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMemberId } from '@trello/authentication';
import { CheckIcon } from '@trello/nachos/icons/check';
import { Spinner } from '@trello/nachos/spinner';

import { useUpdateWorkspaceMemberRole } from './useUpdateWorkspaceMemberRole';
import { useWorkspaceChangeRoleMembershipFragment } from './WorkspaceChangeRoleMembershipFragment.generated';

import styles from './ChangeRolePopoverContent.less';

interface ChangeRolePopoverContentProps {
  hide: () => void;
  showAdminRoleDemotionConfirmation: () => void;
  workspaceId: string;
  memberId: string;
  membershipType: string;
  isWorkspaceAdmin: boolean;
}

const SoleAdminDisclaimer: FunctionComponent = () => {
  return (
    <>
      <hr role="presentation" />
      <p className={styles.soleAdminDisclaimer}>
        <FormattedMessage
          id="templates.workspace_member_roles.must-be-at-least-one-admin"
          defaultMessage="You can't change roles because there must be at least one admin."
          description="You can't change roles because there must be at least one admin."
        />
      </p>
    </>
  );
};

export const ChangeRolePopoverContent: FunctionComponent<
  ChangeRolePopoverContentProps
> = ({
  hide,
  showAdminRoleDemotionConfirmation,
  workspaceId,
  memberId,
  isWorkspaceAdmin,
}) => {
  const myMemberId = useMemberId();
  const { data: workspaceData } = useWorkspaceChangeRoleMembershipFragment({
    from: { id: workspaceId },
  });
  const isSubjectSoleAdmin =
    isWorkspaceAdmin &&
    !workspaceData?.memberships?.find(
      (membership) =>
        membership.idMember !== memberId && membership.memberType === 'admin',
    );

  const { updateWorkspaceMemberRole, loading } = useUpdateWorkspaceMemberRole({
    workspaceId,
    memberId,
  });

  const handleRoleChange = useCallback(
    (newMembershipType: 'admin' | 'normal') => () => {
      if (
        isWorkspaceAdmin &&
        newMembershipType === 'normal' &&
        myMemberId === memberId
      ) {
        showAdminRoleDemotionConfirmation();
        return;
      }
      updateWorkspaceMemberRole({
        memberType: newMembershipType,
        onSuccess: hide,
      });
    },
    [
      isWorkspaceAdmin,
      memberId,
      myMemberId,
      showAdminRoleDemotionConfirmation,
      hide,
      updateWorkspaceMemberRole,
    ],
  );
  if (loading) {
    return <Spinner centered />;
  }
  return (
    <div className={styles.container}>
      <ul>
        <li
          className={cx({
            [styles.roleOption]: true,
            [styles.disabled]: isWorkspaceAdmin,
          })}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
          role="option"
          aria-selected={isWorkspaceAdmin}
          onClick={handleRoleChange('admin')}
        >
          <a>
            <FormattedMessage
              id="templates.workspace_member_roles.admin.title"
              defaultMessage="Admin"
              description="Admin"
            />
            {isWorkspaceAdmin && (
              <span className={styles.checkIcon}>
                <CheckIcon size="small" />
              </span>
            )}
            <span className={styles.roleDescription}>
              <FormattedMessage
                id="templates.workspace_member_roles.admin.text"
                defaultMessage="Can view, create and edit Workspace boards, and change settings for the Workspace. Will have admin rights on all boards in this Workspace."
                description="Admin member workspace role description"
              />
            </span>
          </a>
        </li>
        <li
          className={cx({
            [styles.roleOption]: true,
            [styles.disabled]: !isWorkspaceAdmin || isSubjectSoleAdmin,
          })}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
          role="option"
          aria-selected={!isWorkspaceAdmin}
          onClick={handleRoleChange('normal')}
        >
          <a>
            <FormattedMessage
              id="templates.workspace_member_roles.normal.title"
              defaultMessage="Normal"
              description="Normal"
            />
            {!isWorkspaceAdmin && (
              <span className={styles.checkIcon}>
                <CheckIcon size="small" />
              </span>
            )}
            <span className={styles.roleDescription}>
              <FormattedMessage
                id="templates.workspace_member_roles.normal.text"
                defaultMessage="Can view, create, and edit Workspace boards, but not change settings."
                description="Normal member workspace role description"
              />
            </span>
          </a>
        </li>
      </ul>
      {isSubjectSoleAdmin && <SoleAdminDisclaimer />}
    </div>
  );
};
